import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { useSessionAsync } from 'src/app/shared/hooks/session';
import { isLoaded } from 'src/app/shared/utils/async';

import { useMembershipModalState } from '../../../../modalContext';

type Result = {
	headline: string;
	description: string;
};

type I18nUtils = Pick<ReturnType<typeof useI18n>, 'translate' | 'translatePlural' | 'localizeNumber'>;

function getLocalizedHeadline(
	totalSignatureCount: number,
	usStateName: string,
	{ translatePlural, localizeNumber }: I18nUtils,
): string {
	return translatePlural('corgi.co.ppmu.modal.heading_variant_localized', totalSignatureCount, {
		totalSignatureCount: localizeNumber(totalSignatureCount),
		usStateName,
	});
}

export function useHeader(): Result {
	const [{ firstName, ppmuLocalizedHeadlineData, localizedHeadlineExperimentVariant }] = useMembershipModalState();
	const i18n = useI18n();
	const session = useSessionAsync();

	const { headline, description } = useMemo(() => {
		if (!isLoaded(session)) {
			return { headline: '', description: '' };
		}

		if (localizedHeadlineExperimentVariant === 'variant_1' && ppmuLocalizedHeadlineData?.userStateName) {
			return {
				headline: getLocalizedHeadline(
					ppmuLocalizedHeadlineData.totalSignatureCount,
					ppmuLocalizedHeadlineData.userStateName,
					i18n,
				),
				description: i18n.translate('corgi.co.ppmu.modal.subheading_variant'),
			};
		}

		const isGuest = session.value.loginState === 'GUEST';
		const shortDisplayName = session.value.user?.shortDisplayName ?? firstName ?? '';
		const headlineSuffix = isGuest && !firstName ? '_guest' : '';

		return {
			headline: i18n.translate(`corgi.co.ppmu.modal.heading_variant${headlineSuffix}`, {
				name: shortDisplayName,
			}),
			description: i18n.translate('corgi.co.ppmu.modal.subheading_variant'),
		};
	}, [firstName, localizedHeadlineExperimentVariant, ppmuLocalizedHeadlineData, session, i18n]);

	return { headline, description };
}
